import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Check, CreditCard, Heart, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Footer from '../components/Footer';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Store = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState('stripe');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchaseStripe = async (productId) => {
    try {
      const origin = window.location.origin;
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/stripe/checkout`,
        {
          product_id: productId,
          payment_method: 'stripe'
        },
        {
          headers: {
            'Origin': origin
          }
        }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating Stripe checkout:', error);
      toast.error('Failed to initiate payment');
    }
  };

  const handlePurchaseRazorpay = async (productId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payments/razorpay/create-order`, {
        product_id: productId,
        payment_method: 'razorpay'
      });

      const options = {
        key: response.data.key_id,
        amount: response.data.amount,
        currency: response.data.currency,
        order_id: response.data.order_id,
        name: "Ovalu",
        description: "Premium Health Subscription",
        image: "https://avatars.githubusercontent.com/in/1201222?s=120",
        handler: async function (razorpayResponse) {
          try {
            await axios.post(`${BACKEND_URL}/api/payments/razorpay/verify`, {
              razorpay_order_id: razorpayResponse.razorpay_order_id,
              razorpay_payment_id: razorpayResponse.razorpay_payment_id,
              razorpay_signature: razorpayResponse.razorpay_signature
            });
            toast.success('Payment successful!');
            window.location.href = '/payment/success';
          } catch (error) {
            console.error('Payment verification failed:', error);
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: "",
          email: "",
          contact: ""
        },
        theme: {
          color: "#FF6B9D"
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Error creating Razorpay order:', error);
      toast.error('Failed to initiate payment');
    }
  };

  const handlePurchase = (productId) => {
    if (selectedPayment === 'stripe') {
      handlePurchaseStripe(productId);
    } else {
      handlePurchaseRazorpay(productId);
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      subscription: 'bg-purple-100 text-purple-700',
      course: 'bg-blue-100 text-blue-700',
      digital: 'bg-green-100 text-green-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FFF5F8] to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-[#FF6B9D] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F8] to-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-pink-100">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] rounded-2xl flex items-center justify-center">
                <span className="text-white text-xl font-bold">O</span>
              </div>
              <span className="text-2xl font-bold text-[#FF6B9D]">ovalu</span>
            </Link>
            
            <Link to="/">
              <Button variant="ghost" className="text-gray-700 hover:text-[#FF6B9D]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#FF6B9D] to-[#FFB6C1] rounded-3xl flex items-center justify-center shadow-xl">
              <ShoppingCart className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">Wellness Store</h1>
            <p className="text-xl text-gray-600">
              Premium digital products to empower your health journey
            </p>
          </div>

          {/* Payment Method Selector */}
          <div className="max-w-md mx-auto mb-12">
            <Card className="p-6 border-pink-100">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CreditCard className="w-5 h-5 mr-2 text-[#FF6B9D]" />
                Select Payment Method
              </h3>
              <div className="flex gap-4">
                <button
                  onClick={() => setSelectedPayment('stripe')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    selectedPayment === 'stripe'
                      ? 'border-[#FF6B9D] bg-pink-50'
                      : 'border-gray-200 hover:border-pink-200'
                  }`}
                >
                  <div className="font-semibold text-gray-900">Stripe</div>
                  <div className="text-sm text-gray-600">Card payments</div>
                </button>
                <button
                  onClick={() => setSelectedPayment('razorpay')}
                  className={`flex-1 p-4 rounded-xl border-2 transition-all ${
                    selectedPayment === 'razorpay'
                      ? 'border-[#FF6B9D] bg-pink-50'
                      : 'border-gray-200 hover:border-pink-200'
                  }`}
                >
                  <div className="font-semibold text-gray-900">Razorpay</div>
                  <div className="text-sm text-gray-600">UPI, Cards & More</div>
                </button>
              </div>
            </Card>
          </div>

          {/* Impact Integration */}
          <Card className="max-w-4xl mx-auto mb-12 p-6 border-pink-100 bg-pink-50/30 overflow-hidden relative group">
            <div className="absolute top-0 right-0 p-4 transform translate-x-4 -translate-y-4 opacity-10 group-hover:opacity-20 transition-opacity">
               <Heart className="w-32 h-32 text-[#FF6B9D] fill-[#FF6B9D]" />
            </div>
            <div className="flex flex-col md:flex-row items-center gap-6 relative z-10">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-md flex-shrink-0 animate-bounce">
                <Heart className="w-8 h-8 text-[#FF6B9D] fill-[#FF6B9D]" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Make Your Purchase Mean More ❤️</h3>
                <p className="text-gray-600 text-sm">
                  Add ₹20 to any purchase to support menstrual health awareness for a rural girl in India. 
                  <Link to="/contribute" className="text-[#FF6B9D] ml-1 font-bold hover:underline">Learn more about our mission →</Link>
                </p>
              </div>
              <Link to="/contribute">
                <Button className="bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white rounded-xl px-8">
                  Support Now
                </Button>
              </Link>
            </div>
          </Card>

          {/* Products Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <Card key={product.id} className="overflow-hidden border-pink-100 hover:shadow-xl transition-all duration-300 flex flex-col">
                {product.image_url && (
                  <div className="h-48 overflow-hidden bg-gradient-to-br from-pink-100 to-purple-100">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                )}
                
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-3">
                    <Badge className={getCategoryBadge(product.category)}>
                      {product.category}
                    </Badge>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-[#FF6B9D]">
                        ${product.price}
                      </div>
                      <div className="text-xs text-gray-500 uppercase">
                        {product.currency}
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">{product.name}</h3>
                  <p className="text-gray-600 mb-4 flex-1">{product.description}</p>

                  <div className="space-y-2 mb-6">
                    {product.features.slice(0, 5).map((feature, index) => (
                      <div key={index} className="flex items-start text-sm text-gray-700">
                        <Check className="w-4 h-4 mr-2 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>
                    ))}
                    {product.category === 'subscription' && (
                      <div className="flex items-center text-xs text-[#FF6B9D] font-medium pt-2 italic">
                        <Star className="w-3 h-3 mr-1 fill-[#FF6B9D]" />
                        Subscription will auto-renew unless cancelled
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => handlePurchase(product.id)}
                    className="w-full bg-[#FF6B9D] hover:bg-[#ff5a8c] text-white"
                  >
                    Purchase Now
                  </Button>
                </div>
              </Card>
            ))}
          </div>

          {/* Trust Section */}
          <div className="mt-20 text-center">
            <Card className="p-8 border-pink-100 bg-gradient-to-br from-pink-50 to-white max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Secure Payment Guaranteed</h3>
              <p className="text-gray-600 mb-4">
                Your payment information is encrypted and secure. We never store your card details.
              </p>
              <div className="flex justify-center items-center gap-8 text-sm text-gray-600">
                <div className="flex items-center">
                  <Check className="w-5 h-5 mr-2 text-green-600" />
                  SSL Encrypted
                </div>
                <div className="flex items-center">
                  <Check className="w-5 h-5 mr-2 text-green-600" />
                  PCI Compliant
                </div>
                <div className="flex items-center">
                  <Check className="w-5 h-5 mr-2 text-green-600" />
                  Money Back Guarantee
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Store;
